import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

import { IEmailPreview } from 'src/app/models/email';
import { ApiLotsService } from 'src/app/services/api/api-lots.service';

export interface IPreviewEmailTab {
  BatchId: string;
  BatchNumber: string;
  BeneficiaryName?: string;
}

export interface IModalPreviewEmailData {
  title: string;
  paymentEmail: boolean;
  /** Lote (todos os sublotes) ou um sublote só. É o mesmo id do botão de envio. */
  batchId: string;
  /** Sublotes já conhecidos pela tela, para o modal abrir com as abas antes da API responder. */
  tabs: IPreviewEmailTab[];
}

@Component({
  selector: 'app-modal-preview-email-v2',
  templateUrl: './modal-preview-email-v2.component.html',
  styleUrls: ['./modal-preview-email-v2.component.scss']
})
export class ModalPreviewEmailV2Component implements OnInit, OnDestroy {
  tabs: IPreviewEmailTab[] = [];
  selectedIndex = 0;

  /** Prévias por `BatchId`, preenchidas conforme as respostas chegam. */
  previews = new Map<string, IEmailPreview>();
  failed = new Set<string>();

  /** Sublotes que já têm requisição em andamento (para não pedir duas vezes). */
  private _requested = new Set<string>();
  private _subscriptions: Subscription[] = [];

  /** Corpo de cada prévia, já liberado para o `srcdoc` (ver `bodyFor`). */
  private _bodies = new Map<string, SafeHtml>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IModalPreviewEmailData,
    public dialogRef: MatDialogRef<ModalPreviewEmailV2Component>,
    private _sanitizer: DomSanitizer,
    private _batchService: ApiLotsService
  ) {}

  /**
   * Cada sublote custa ~1–2 s na API (busca do parceiro no CredIntegrados), e o
   * lote inteiro passava de 10 s. O modal abre na hora: a primeira aba é pedida
   * sozinha e aparece primeiro, e o lote inteiro vem em paralelo para as demais.
   */
  ngOnInit(): void {
    this.tabs = [...this.data.tabs];

    if (this.tabs.length > 1) {
      this.load(this.tabs[0].BatchId);
    }

    const covers = this.tabs.map((tab) => tab.BatchId);

    this.load(this.data.batchId, covers.length ? covers : [this.data.batchId]);
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get selected(): IPreviewEmailTab | undefined {
    return this.tabs[this.selectedIndex];
  }

  get loadedCount(): number {
    return this.tabs.filter((tab) => this.previews.has(tab.BatchId)).length;
  }

  get sendCount(): number {
    return this.tabs.filter((tab) => this.previews.get(tab.BatchId)?.WillSend).length;
  }

  preview(tab: IPreviewEmailTab): IEmailPreview | undefined {
    return this.previews.get(tab.BatchId);
  }

  isLoading(tab: IPreviewEmailTab): boolean {
    return !this.previews.has(tab.BatchId) && !this.failed.has(tab.BatchId);
  }

  /** Aba ainda sem resposta ganha uma requisição própria, em vez de esperar o lote inteiro. */
  selectTab(index: number): void {
    this.selectedIndex = index;

    const tab = this.tabs[index];

    if (tab && !this.previews.has(tab.BatchId) && !this._requested.has(tab.BatchId)) {
      this.load(tab.BatchId);
    }
  }

  /**
   * `covers`: sublotes que esta requisição responde. Se ela falhar, eles
   * aparecem com erro (a menos que outra requisição já os tenha trazido).
   */
  private load(batchId: string, covers: string[] = [batchId]): void {
    covers.forEach((id) => this._requested.add(id));

    const subscription = this._batchService
      .getTransferBatchEmailPreview(batchId, this.data.paymentEmail)
      .subscribe({
        next: (previews) => {
          previews.forEach((preview) => {
            const id = String(preview.BatchId);

            this.previews.set(id, preview);
            this.failed.delete(id);
            this._bodies.delete(id);

            // A lista da tela e a da API deveriam bater; se a API trouxer um
            // sublote a mais, ele ganha aba em vez de sumir.
            if (!this.tabs.some((tab) => tab.BatchId === id)) {
              this.tabs.push({
                BatchId: id,
                BatchNumber: preview.BatchNumber,
                BeneficiaryName: preview.BeneficiaryName
              });
            }
          });
        },
        error: () => {
          covers
            .filter((id) => !this.previews.has(id))
            .forEach((id) => {
              this.failed.add(id);
              this._requested.delete(id);
            });
        }
      });

    this._subscriptions.push(subscription);
  }

  /**
   * O HTML do template vai inteiro para um `iframe` com `sandbox` vazio: sem
   * script, sem formulário e sem navegação, e o CSS do e-mail não vaza para a
   * tela. Por isso o bypass do sanitizer é seguro aqui (e o sanitizer cortaria
   * o `<style>` do e-mail).
   */
  bodyFor(preview: IEmailPreview): SafeHtml {
    const id = String(preview.BatchId);

    if (!this._bodies.has(id)) {
      this._bodies.set(id, this._sanitizer.bypassSecurityTrustHtml(preview.Body ?? ''));
    }

    return this._bodies.get(id)!;
  }

  recipients(preview: IEmailPreview): string[] {
    return (preview.UsersTo ?? '').split(';').filter((email) => !!email.trim());
  }

  attachmentName(url: string): string {
    return decodeURIComponent(url.split('?')[0].split('/').pop() || url);
  }
}
